import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import WarningTriangle from '../../../components/WarningTriangle';

import { DataGrid, GridActionsCellItem, GRID_STRING_COL_DEF } from '@mui/x-data-grid';
import CheckIcon from '@mui/icons-material/Check';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';

import Popper from '@mui/material/Popper';
import { Box, Button, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import EditIcon from '@mui/icons-material/Edit';

import FixOrContinueModal from '../Modals/FixOrContinueModal';

import { GBENSContext } from '../GBENSContainer';
import { useStepper } from '../../../components/Stepper';

import RightLink from '../Components/RightLink';
import { statuses } from '../../../utils/constants';

function StatusCell({ value, row }) {
  const errorsValidation = value[0];
  const errorsThymeIT = value[1];
  const hasThymeError = row?.statusDescription?.toUpperCase() === statuses.ERRORS;

  if (!errorsValidation?.errors && !errorsThymeIT?.errors && !hasThymeError)
    return <CheckIcon sx={{ color: 'green' }} />;

  return (
    <>
      {errorsValidation?.errors && (
        <WarningTriangle
          message={errorsValidation?.message}
          errors={Object.keys(errorsValidation?.errors ?? {})
            .filter((k) => k !== 'lines' && k !== 'id')
            .map((k) => errorsValidation?.errors[k])}
        />
      )}
      {hasThymeError && <WarningTriangle message={errorsThymeIT?.message} color="red" />}
    </>
  );
}

StatusCell.propTypes = {
  value: PropTypes.object,
};

const statusColumnType = {
  ...GRID_STRING_COL_DEF,
  type: 'custom',
  resizable: false,
  filterable: false,
  sortable: false,
  editable: false,
  groupable: false,
  display: 'flex',
  renderCell: (params) => <StatusCell {...params} />,
};

const Consignments = ({ setConsignmentsVisited }) => {
  const [selectedConsignments, setSelectedConsignments] = useState([]);
  const [FixOrContinueModalOpen, setFixOrContinueModalOpen] = useState(false);
  const { gotoStep } = useStepper();

  const {
    data,
    setData,
    errors,
    errorsHMRC,
    hasErrors,
    addNewConsignment,
    editConsignment,
    deleteConsignment,
    isEditable,
    isReadOnly,
    possibleToAddConsignment,
  } = useContext(GBENSContext);

  useEffect(() => {
    setConsignmentsVisited(true);
  }, []);

  const disableFields = isReadOnly;

  const mappedConsignmentData = useMemo(() => {
    return [...(data?.Consignments ?? [])].map((c) => ({
      id: c.id,
      consignmentReference: c.consignmentReference,
      consignmentNumber: c.consignmentNumber,
      consignor: c.Consignor?.companyName,
      consignee: c.Consignee?.companyName,
      lines: c.lines?.length ?? 0,
      isEditable: c.isEditable,
      status: [
        {
          message: 'There are errors with this consignment.',
          errors:
            errors?.Consignments?.find?.(
              (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
            ) ?? null,
        },
        {
          message: 'There are ICS2 errors with this consignment.',
          errors:
            errorsHMRC?.Consignments?.find?.(
              (e) => e.id === c.id && Object.keys(e).filter((k) => k !== 'id' && k !== 'lines').length > 0
            ) ?? null,
        },
      ],
      statusDescription: c.statusDescription,
    }));
  }, [data, errors]);

  const handleSelectionChange = (selectionModel) => {
    setSelectedConsignments(selectionModel);
  };

  const handleDelete = async () => {
    if (selectedConsignments.length === 0) {
      alert('Please select consignments to delete.');
      return;
    }

    try {
      await deleteConsignment(selectedConsignments);
      // Convert the selected IDs to numbers to match consignment.id
      const selectedIds = selectedConsignments.map((id) => Number(id));

      // Update the context state by filtering out deleted consignments.
      setData((prevData) => ({
        ...prevData,
        Consignments: prevData.Consignments.filter((consignment) => !selectedIds.includes(consignment.id)),
      }));
      setSelectedConsignments([]); // Clear selection after deletion
    } catch (error) {
      console.error('Error deleting consignments:', error);
      alert('An error occurred while deleting consignments.');
    }
  };

  const updateConsignment = (id) => {
    editConsignment(id);
    gotoStep('UpdatingConsignment');
  };

  const newConsignment = () => {
    if (!possibleToAddConsignment?.isValid) {
      alert(possibleToAddConsignment?.message || 'You cannot add a new consignment at this time.');
      return;
    }
    addNewConsignment();
    gotoStep('AddingConsignment');
  };

  const onNext = () => {
    if (hasErrors('Consignments') && !disableFields) {
      setFixOrContinueModalOpen(true);
    } else {
      onContinue();
    }
  };

  const onContinue = () => {
    gotoStep('ReviewAndSubmit');
    setFixOrContinueModalOpen(false);
  };

  const renderCell = (params) => {
    return (
      <Box sx={{ ...(params?.row?.statusDescription?.toUpperCase() === statuses.ERRORS ? { color: '#E61610' } : {}) }}>
        {params.value}
      </Box>
    );
  };

  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      ...statusColumnType,
      headerClassName: 'table-header',
      width: 75,
      minWidth: 75,
      maxWidth: 75,
    },
    {
      field: 'consignmentNumber',
      headerName: '#',
      flex: 1,
      headerClassName: 'table-header',
      width: 75,
      minWidth: 75,
      maxWidth: 75,
      renderCell,
    },
    {
      field: 'consignmentReference',
      headerName: 'Reference',
      flex: 1,
      headerClassName: 'table-header',
      renderCell,
    },
    {
      field: 'consignor',
      headerName: 'Consignor',
      flex: 1,
      headerClassName: 'table-header',
      renderCell,
    },
    { field: 'consignee', headerName: 'Consignee', flex: 1, headerClassName: 'table-header', renderCell },
    { field: 'lines', headerName: '# of lines', flex: 1, headerClassName: 'table-header', renderCell },
    { field: 'statusDescription', headerName: 'Status', flex: 1, headerClassName: 'table-header', renderCell },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 1,
      headerClassName: 'table-header',
      type: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem key={id} icon={<EditIcon />} label="Edit" onClick={() => updateConsignment(id)} />,
        ];
      },
    },
  ];
  return (
    <>
      <FixOrContinueModal
        open={FixOrContinueModalOpen}
        onFix={() => setFixOrContinueModalOpen(false)}
        onSave={onContinue}
      />
      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <div style={{ width: '100%', marginBottom: 30 }}>
          <Typography id="modal-modal-title" variant="p" component="p" style={{ marginLeft: 3 }}>
            Consignments added to this ICS 2 declaration are displayed below:
          </Typography>
          <Paper sx={{ width: '100%', marginBottom: 2 }}>
            <DataGrid
              rows={mappedConsignmentData}
              columns={columns}
              pageSizeOptions={[20, 50, 100]}
              checkboxSelection
              isRowSelectable={(params) => !disableFields && !!params.row.isEditable}
              onRowSelectionModelChange={handleSelectionChange}
              sx={{ border: 0 }}
            />
          </Paper>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {!disableFields ? <RightLink name="Add Consignment" onClick={newConsignment} /> : <span></span>}
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={selectedConsignments.length === 0}
              startIcon={<DeleteIcon />}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 30,
        }}
      >
        <div></div>
        <Button variant="outlined" onClick={onNext}>
          Next: Review & Submit
        </Button>
      </div>
    </>
  );
};

Consignments.propTypes = {
  setConsignmentsVisited: PropTypes.func.isRequired,
};

export default Consignments;
